import { useEffect, useRef, useState } from "react";
import { OFFICE_ACTIONS, type OfficeActionId } from "@/lib/office/actions";
import type { VyllageSceneState } from "../use-vyllage-state";
import {
  CamIcon,
  DecorIcon,
  DoorIcon,
  LaptopIcon,
  MicIcon,
  PersonIcon,
  WaveIcon,
} from "./scene-icons";

/** Bottom-center control dock: mic / cam / actions / edit avatar, plus the owner's
 *  laptop / door / customize when in their own office. */
export function ActionDock({ s }: { s: VyllageSceneState }) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!actionsOpen) return;
    const onDoc = (ev: MouseEvent) => {
      const root = actionsMenuRef.current;
      if (root && !root.contains(ev.target as Node)) setActionsOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setActionsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [actionsOpen]);

  return (
    <div className="absolute bottom-3.5 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-[var(--vy-dock)] px-2.5 py-2 backdrop-blur-sm">
      <DockButton
        title="Microphone"
        onClick={s.toggleMic}
        bg={s.mic ? "var(--vy-glass-strong)" : "var(--vy-bad)"}
      >
        <MicIcon />
      </DockButton>
      <DockButton
        title="Camera"
        onClick={s.toggleCam}
        bg={s.cam ? "var(--vy-glass-strong)" : "var(--vy-bad)"}
      >
        <CamIcon />
      </DockButton>
      <span className="h-6 w-px bg-white/14" />
      <div className="relative" ref={actionsMenuRef}>
        <DockButton
          title="Actions (E / 1–6)"
          onClick={() => setActionsOpen((v) => !v)}
          bg={actionsOpen ? "var(--vy-accent-border)" : "var(--vy-glass-strong)"}
        >
          <WaveIcon />
        </DockButton>
        {actionsOpen ? (
          <ActionsMenu
            onSelect={(id) => {
              s.performAction(id);
              setActionsOpen(false);
            }}
          />
        ) : null}
      </div>
      <DockButton
        title="Edit avatar"
        onClick={() => s.setShowOnboarding(true)}
        bg="var(--vy-glass-strong)"
      >
        <PersonIcon />
      </DockButton>
      {s.myOffice ? (
        <>
          <span className="h-6 w-px bg-white/14" />
          {s.inOwnOffice ? (
            <DockButton
              title="Open desk laptop (app bookmarks)"
              onClick={() => {
                s.setMeetingViewOpen(false);
                s.setLaptopOpen(true);
              }}
              bg={s.laptopOpen ? "var(--vy-accent)" : "var(--vy-accent-bg)"}
            >
              <LaptopIcon />
            </DockButton>
          ) : null}
          <DockButton
            title={
              s.myOffice.doorStatus === "open"
                ? "Close your office (no guests)"
                : "Open your office to guests"
            }
            onClick={s.toggleOfficeDoor}
            bg={s.myOffice.doorStatus === "open" ? "var(--vy-good-border)" : "rgba(216,68,60,0.45)"}
          >
            <DoorIcon open={s.myOffice.doorStatus === "open"} />
          </DockButton>
          <DockButton
            title="Customize your office"
            onClick={() => s.setShowCustomizeOffice(true)}
            bg="var(--vy-glass-strong)"
          >
            <DecorIcon />
          </DockButton>
        </>
      ) : null}
    </div>
  );
}

function DockButton({
  title,
  onClick,
  bg,
  children,
}: {
  title: string;
  onClick: () => void;
  bg: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 hover:brightness-125"
      style={{ background: bg }}
    >
      {children}
    </button>
  );
}

function ActionsMenu({ onSelect }: { onSelect: (id: OfficeActionId) => void }) {
  return (
    <div
      className="absolute bottom-[calc(100%+10px)] left-1/2 z-50 w-[220px] -translate-x-1/2 overflow-hidden rounded-xl border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] shadow-[var(--shadow-md)]"
      role="menu"
      aria-label="Avatar actions"
    >
      <div className="border-b border-[var(--vy-surface-border)] px-3 py-2 text-[10px] font-bold tracking-[0.14em] text-[var(--vy-surface-fg-3)] uppercase">
        Actions
      </div>
      <ul className="max-h-[280px] overflow-auto py-1">
        {OFFICE_ACTIONS.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              role="menuitem"
              className="flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3 py-2 text-left hover:bg-[var(--vy-surface-hover)]"
              onClick={() => onSelect(a.id)}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#FFD65A]/25 text-[14px]">
                {a.emoji}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-semibold text-[var(--vy-surface-fg)]">
                  {a.label}
                </span>
                <span className="block truncate text-[10.5px] text-[var(--vy-surface-fg-3)]">
                  {a.description}
                </span>
              </span>
              <kbd className="rounded border border-[var(--vy-surface-border)] bg-[var(--vy-surface-hover)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--vy-surface-fg-3)]">
                {a.id === "wave" ? "E" : a.shortcut}
              </kbd>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
